import type {FC} from "react";
import {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {Captcha} from "@src/components/captcha";

import {joiResolver} from "@hookform/resolvers/joi";
import {
    BorderForm,
    Container,
    ContentContainer,
    PageTitle,
    StyledTextField,
    Title,
    palette,
    ContainerSpaceBetween,
    SocialAuthGroup,
    SocialAuthTitle,
    SocialAuthTitleText,
    Line,
    SocialButtonGroup,
    VkButton,
    YandexButton,
    GoogleButton
} from "@src/core/styles";
import {
    CheckBoxWrapper,
    FormFooter,
    FormTitleWrapper,
    ButtonText,
    PageTitleWrapper,
    InputGroup,
    LoginForm
} from "./login-page.styles";
import type {
    LoginErrorResponse,
    LoginErrors,
    LoginFormValues,
} from "@src/core/types";
import {authSchema} from "@src/core/schemas";
import {Button, CheckBox} from "@src/components";
import {ReactComponent as UserIcon} from "@src/core/icons/user-outlined.svg";
import {useMutation} from "@tanstack/react-query";
import {Authentication} from "@src/core/services";
import {APP_ROUTES} from "@src/core/constants";
import {setLocalStorage} from "@src/core/utils";
import {FormContainer} from "../route-management-page/components/form-component/form-components.styles";
import {AxiosError} from "axios";
import {ReactComponent as Vk} from "@src/core/icons/vk.svg";
import {ReactComponent as Yandex} from "@src/core/icons/yandex.svg";
import {ReactComponent as Google} from "@src/core/icons/google.svg";
import {ReactComponent as Ok} from "@src/core/icons/ok.svg";

export const Login: FC = () => {
    const navigate = useNavigate();

    const [checked, setChecked] = useState<boolean>(false);
    const [errorsRequest, setErrorsRequest] = useState<LoginErrors | undefined>();
    const [captchaState, setCaptchaState] = useState<boolean>(false);

    const {
        handleSubmit,
        control,
        formState: {errors, isValid},
    } = useForm<LoginFormValues>({
        mode: "onChange",
        resolver: joiResolver(authSchema),
        defaultValues: {
            login: "",
            password: "",
        },
    });

    const captchaValidation = (value: any) => {
        setCaptchaState(value);

        return value;
    }
    const fullValidation = isValid && captchaState;

    const {mutate} = useMutation(Authentication.login, {
        onSuccess: (response) => {
            setLocalStorage("userName", response.data.user.first_name);
            return window.location.replace('/dashboard');

            // navigate(`${APP_ROUTES.DASHBOARD}`);
        },
        onError: (error: AxiosError<LoginErrorResponse>) => {
            setErrorsRequest(error?.response?.data.errors);
            console.log(error?.response?.data.errors);
            console.log(errorsRequest)
        },
    });

    const typePasswordField = checked ? "text" : "password";

    const changeHandler = () => {
        setChecked(!checked);
    };

    const navigateHandler = () => {
        navigate(APP_ROUTES.REGISTRATION);
    };

    const onSubmit = (data: LoginFormValues) => {
        mutate(data);
    };

    const socialHandle = (type: string) => {
        return window.location.replace('/social-auth/'+type)
    }

    const socialHandleVK = () => {
        return window.location.replace('/social-auth/vkontakte');
    }

    const socialHandleYandex = () => {
        return window.location.replace('/social-auth/yandex');
    }

    const socialHandleGoogle = () => {
        return window.location.replace('/social-auth/google');
    }

    return (
        <Container>
            <ContentContainer>
                <FormContainer>
                    <PageTitleWrapper>
                        <PageTitle>Добро пожаловать!</PageTitle>
                    </PageTitleWrapper>
                    <ContainerSpaceBetween>
                        <BorderForm>
                            <FormTitleWrapper>
                                <Title>Вход</Title>
                                <Link to={`${APP_ROUTES.RECOVERY_PASSWORD}`}>
                                    <ButtonText>Забыли пароль?</ButtonText>
                                </Link>
                            </FormTitleWrapper>
                            <LoginForm>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <InputGroup>
                                        <Controller
                                            control={control}
                                            name="login"
                                            render={({field: {value, onBlur, onChange}}) => (
                                                <StyledTextField
                                                    onBlur={onBlur}
                                                    variant="outlined"
                                                    placeholder="user@test.com"
                                                    label="Логин"
                                                    focused={value ? true : false}
                                                    InputLabelProps={{
                                                        sx: {
                                                            "&.Mui-focused": {
                                                                color: `${palette.colors.green_base}`,
                                                            },
                                                            "&.Mui-error": {color: `${palette.colors.error}`},
                                                        },
                                                    }}
                                                    value={value}
                                                    error={!!errors?.login?.message || !!errorsRequest?.login}
                                                    helperText={
                                                        errors?.login?.message || errorsRequest?.login
                                                    }
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={control}
                                            name="password"
                                            render={({field: {value, onBlur, onChange}}) => (
                                                <StyledTextField
                                                    onBlur={onBlur}
                                                    variant="outlined"
                                                    type={typePasswordField}
                                                    label="Пароль"
                                                    focused={value ? true : false}
                                                    placeholder="Введите пароль"
                                                    InputLabelProps={{
                                                        sx: {
                                                            "&.Mui-focused": {
                                                                color: `${palette.colors.green_base}`,
                                                            },
                                                            "&.Mui-error": {color: `${palette.colors.error}`},
                                                        },
                                                    }}
                                                    value={value}
                                                    error={
                                                        !!errors?.password?.message || !!errorsRequest?.password
                                                    }
                                                    helperText={
                                                        errors?.password?.message || errorsRequest?.password
                                                    }
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                    </InputGroup>
                                    <CheckBoxWrapper>
                                        <CheckBox
                                            label="Показать пароль"
                                            checked={checked}
                                            onChange={changeHandler}
                                        />
                                    </CheckBoxWrapper>

                                    <FormFooter>
                                        <Captcha CaptchaState={captchaValidation}/>
                                        <Button
                                            text="Войти"
                                            type="submit"
                                            icon={<UserIcon/>}
                                            borderradius="L"
                                            disabled={!fullValidation}
                                            variant="contained"
                                        />
                                        <ButtonText onClick={navigateHandler}>Регистрация</ButtonText>
                                    </FormFooter>
                                </form>
                            </LoginForm>
                        </BorderForm>
                        <SocialAuthGroup>
                            <SocialAuthTitle>
                                <Line/>
                                <SocialAuthTitleText>Вход&nbsp;через</SocialAuthTitleText>
                                <Line/>
                            </SocialAuthTitle>
                            <SocialButtonGroup>
                                {/* VK ID временно отключен: авторизация не работает, пока не настроен доступ к email в кабинете VK ID */}
                                {/*<Link to="#" onClick={socialHandleVK}>*/}
                                {/*    <VkButton>*/}
                                {/*        <Vk/>*/}
                                {/*        VK ID*/}
                                {/*    </VkButton>*/}
                                {/*</Link>*/}
                                <Link to="#" onClick={socialHandleYandex}>
                                    <YandexButton>
                                        <Yandex/>
                                        Яндекс ID
                                    </YandexButton>
                                </Link>
                                {/*<Link to="#" onClick={socialHandleGoogle}>*/}
                                {/*    <GoogleButton>*/}
                                {/*        <Google/>*/}
                                {/*        Google*/}
                                {/*    </GoogleButton>*/}
                                {/*</Link>*/}
                            </SocialButtonGroup>
                        </SocialAuthGroup>
                    </ContainerSpaceBetween>
                </FormContainer>
            </ContentContainer>
        </Container>
    );
};
